/**
 * @fileoverview Own draft editing of matter metadata, stage context, attorneys, and defendants. The backend owns accepted matter/party identity and the parent owns refreshed case context. Browser caches support lookup presentation without authorizing saved changes. Explicit save and cancel keep local edits separate from canonical matter state.
 */

import React, { useState, useEffect, useContext } from 'react';
import ToastContext from '../../ToastContext';
import api from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import TrelloTaskForm from './TrelloTaskForm';
import {
	setLoading as setAppLoading,
} from '../../store/App/action';
import { AppButton, AppDropdown, AppTextInput, AppAutoComplete } from '../shared';
import './PartiesPanel.css';

// Cache configuration for attorneys and defendants lists.
// These are shared across all cases, so we cache them globally for instant loading.
const ATTORNEYS_CACHE_KEY = 'attorneys_list_cache'
const DEFENDANTS_CACHE_KEY = 'defendants_list_cache'
const CACHE_STALE_MS = 5 * 60 * 1000 // 5 minutes


const stages = {
	1: 'Pre-Demand',
	2: 'Demand Sent',
	3: 'Arbitration-AAA',
	4: 'Arbitration-JAMS',
	5: 'Arbitration-Other',
	6: 'State Court',
	7: 'Federal Court',
	8: 'Appeal',
	9: 'Post Judgment',
	10: 'Settled/Resolved',
	11: 'Withdrawn'
};

const courtSubstages = {
	1: 'Complaint Filed',
	2: 'Service Complete',
	3: 'Motion to Dismiss Filed',
	4: 'Discovery',
	5: 'Stayed',
	6: 'Dispositive Motion Pending',
	7: 'Trial Upcoming',
	8: 'Appeal'
};

const settledSubstages = {
	9: 'Settled in Principal',
	10: 'Agreement Executed & Payment(s) Pending',
	11: 'Payments Complete/Fully Resolved'
};



const getOptions = (items, labelField, valueField, keyField) =>
	items.map(item => ({
		label: item[labelField],
		value: item[valueField],
		key: item[keyField]
	}))



const useMatterAttributes = (matter, allAttorneys, allDefendants) => {
	const [attributes, setAttributes] = useState([])
	const [selectedAttorneys, setSelectedAttorneys] = useState([])
	const [selectedDefendants, setSelectedDefendants] = useState([])
	const [localJurisdiction, setLocalJurisdiction] = useState('')
	const [localCaseNumber, setLocalCaseNumber] = useState('')
	const [localStage, setLocalStage] = useState('')
	const [localSubstage, setLocalSubstage] = useState('')

	useEffect(

	() => {
		console.log('useMatterAttributes', matter, allAttorneys, allDefendants);
		const newAttributes = [
			{
				label: 'Attorneys',
				key: 'Attorneys',
				value: matter.attorneys,
				options: getOptions(allAttorneys, 'Name', 'id', 'id')
			},
			{
				label: 'Defendants',
				key: 'Defendants',
				value: matter.defendants,
				options: getOptions(allDefendants, 'PartyName', 'id', 'id')
			},
			{ label: 'Jurisdiction', key: 'Jurisdiction', value: matter.Jurisdiction },
			{ label: 'Case Number', key: 'CaseNumber', value: matter.CaseNumber },
			{ label: 'Stage', key: 'Stage', value: matter.Stage },
		]

		if (['State Court', 'Federal Court', 'Settled/Resolved'].includes(matter.Stage)) {
			newAttributes.push({ label: 'Substage', key: 'Substage', value: matter.Substage })
		}

		setAttributes(newAttributes)
		// If different attorneys or defendants are selected, update the state
		if (selectedAttorneys !== matter.attorneys) setSelectedAttorneys(matter.attorneys)
		if (selectedDefendants !== matter.defendants) setSelectedDefendants(matter.defendants)
		if (localJurisdiction !== matter.Jurisdiction) setLocalJurisdiction(matter.Jurisdiction)
		if (localCaseNumber !== matter.CaseNumber) setLocalCaseNumber(matter.CaseNumber)
		if (localStage !== matter.Stage) setLocalStage(matter.Stage)
		if (localSubstage !== matter.Substage) setLocalSubstage(matter.Substage)
	}, [matter, allAttorneys, allDefendants])

	useEffect(

	() => {
		// If user changes stage, update the substage
		if (localStage !== matter.Stage) {
			const newAttributes = [...attributes];
			// If the new stage is not a court stage, remove the substage
			if (!['State Court', 'Federal Court', 'Settled/Resolved'].includes(localStage)) {
				const substageIndex = newAttributes.findIndex(attribute => attribute.key === 'Substage');
				if (substageIndex > -1) {
					newAttributes.splice(substageIndex, 1);
				}
			} else {
				// If the new stage is a court stage, add the substage if it doesn't already exist
				const substageIndex = newAttributes.findIndex(attribute => attribute.key === 'Substage');
				if (substageIndex === -1) {
					newAttributes.push({ label: 'Substage', key: 'Substage', value: '' });
				}
			}
			setAttributes(newAttributes);
		}
	}, [localStage])

	return {
		attributes,
		selectedAttorneys,
		selectedDefendants,
		localJurisdiction,
		localCaseNumber,
		localStage,
		localSubstage,
		setSelectedAttorneys,
		setSelectedDefendants,
		setLocalJurisdiction,
		setLocalCaseNumber,
		setLocalStage,
		setLocalSubstage
	}
}



function CaseInfo({ fetchMatter }) {
	const dispatch = useDispatch();
	const matter = useSelector(state => state.caseDetails.matter);
	const showToast = useContext(ToastContext)
	const [isEditing, setIsEditing] = useState(false)
	const [pendingFocusField, setPendingFocusField] = useState(null)
	const [allAttorneys, setAllAttorneys] = useState([]);
	const [allDefendants, setAllDefendants] = useState([]);

	const {
		attributes,
		selectedAttorneys,
		selectedDefendants,
		localJurisdiction,
		localCaseNumber,
		localStage,
		localSubstage,
		setSelectedAttorneys,
		setSelectedDefendants,
		setLocalJurisdiction,
		setLocalCaseNumber,
		setLocalStage,
		setLocalSubstage
	} = useMatterAttributes(matter, allAttorneys, allDefendants)

	const handleSelectChange =

	(label, value, attributeKey) => {
		if (attributeKey === 'Stage') {
			setLocalStage(value);
		} else if (attributeKey === 'Substage') {
			setLocalSubstage(value);
		}
	};

	useEffect(() => {
		console.log('selectedDefendants', selectedDefendants);
	}, [selectedDefendants]);

	useEffect(() => {
		console.log('selectedAttorneys', selectedAttorneys);
	}, [selectedAttorneys]);



	useEffect(

	() => {
		const loadFromCache =

		(key) => {
			try {
				const cached = localStorage.getItem(key)
				if (!cached) return null
				const { timestamp, data } = JSON.parse(cached)
				// Allow stale data for instant display
				if (Date.now() - timestamp < CACHE_STALE_MS * 2) {
					return data
				}
				return null
			} catch {
				return null
			}
		}

		const saveToCache =

		(key, data) => {
			try {
				localStorage.setItem(key, JSON.stringify({
					timestamp: Date.now(),
					data
				}))
			} catch (e) {
				console.warn(`Failed to cache ${key}:`, e)
			}
		}

		// Try to load from cache first for instant display
		const cachedAttorneys = loadFromCache(ATTORNEYS_CACHE_KEY)
		const cachedDefendants = loadFromCache(DEFENDANTS_CACHE_KEY)

		if (cachedAttorneys) {
			console.log('Loaded attorneys from cache')
			setAllAttorneys(cachedAttorneys)
		}
		if (cachedDefendants) {
			console.log('Loaded defendants from cache')
			setAllDefendants(cachedDefendants)
		}

		// Fetch fresh data in background
		api.get('attorneys')
			.then(response => {
				setAllAttorneys(response.data)
				saveToCache(ATTORNEYS_CACHE_KEY, response.data)
			})
			.catch(error => console.error('An error occurred while fetching attorneys:', error));

		api.get('defendants')
			.then(response => {
				setAllDefendants(response.data)
				saveToCache(DEFENDANTS_CACHE_KEY, response.data)
			})
			.catch(error => console.error('An error occurred while fetching defendants:', error));
	}, []);

	const handleSave =

	async () => {
		dispatch(setAppLoading(true))
		setIsEditing(false);
		setPendingFocusField(null)
		try {
			let m = { ...matter };
			m.defendants = selectedDefendants;
			m.attorneys = selectedAttorneys;
			m.Jurisdiction = localJurisdiction;
			m.CaseNumber = localCaseNumber;
			m.Stage = localStage;
			m.Substage = localSubstage;
			const response = await api.post('case-info', m);
			fetchMatter();

			if (response.status !== 201) {  // assuming 201 for successful resource creation
				showToast({ severity: 'error', summary: 'Error', detail: 'Failed to update case information' });
				dispatch(setAppLoading(false))
				return;
			}

			showToast({ severity: 'success', summary: 'Successful', detail: 'Updated case information' });
			dispatch(setAppLoading(false))
		} catch (error) {
			console.error(error);
			showToast({ severity: 'error', summary: 'Error', detail: 'An error occurred while updating case information' });
			dispatch(setAppLoading(false))
		}
	}

	const handleCancel =

	async () => {
		setPendingFocusField(null)
		await fetchMatter()
		setIsEditing(false)
	}

	const handleStartEditingField =

	(fieldKey) => {
		if (!isEditing) {
			setPendingFocusField(fieldKey)
			setIsEditing(true)
		}
	}

	const substage =

	() => {
		if (['State Court', 'Federal Court'].includes(localStage)) {
			return (
				<AppDropdown
					className="parties-panel__compact-dropdown"
					variant="borderless"
					autoFocus={pendingFocusField === 'Substage'}
					openOnAutoFocus={pendingFocusField === 'Substage'}
					value={localSubstage}
					options={Object.entries(courtSubstages).map(([key, value]) => ({ label: value, value: value }))}
					onChange={(e) => handleSelectChange(e.label, e.value, 'Substage')}
				/>
			)
		} else if (localStage === 'Settled/Resolved') {
			return (
				<AppDropdown
					className="parties-panel__compact-dropdown"
					variant="borderless"
					autoFocus={pendingFocusField === 'Substage'}
					openOnAutoFocus={pendingFocusField === 'Substage'}
					value={localSubstage}
					options={Object.entries(settledSubstages).map(([key, value]) => ({ label: value, value: value }))}
					onChange={(e) => handleSelectChange(e.label, e.value, 'Substage')}
				/>
			)
		}
	}

	const renderAttributeValue =

	(attribute) => {
		if (isEditing) {
			return {
					'Stage': (
						<AppDropdown
							className="parties-panel__compact-dropdown"
							variant="borderless"
							autoFocus={pendingFocusField === 'Stage'}
							openOnAutoFocus={pendingFocusField === 'Stage'}
							value={localStage}
							options={Object.entries(stages).map(([key, value]) => ({ label: value, value: value }))}
							onChange={(e) => handleSelectChange(e.label, e.value, attribute.key)}
						/>
				),
				'Substage': substage(),
				'Attorneys': (
					<AttorneysSelect
						selectedAttorneys={selectedAttorneys}
						setSelectedAttorneys={setSelectedAttorneys}
						allAttorneys={allAttorneys}
						autoFocus={pendingFocusField === 'Attorneys'}
					/>
				),
				'Defendants': (
					<DefendantsSelect
						selectedDefendants={selectedDefendants}
						setSelectedDefendants={setSelectedDefendants}
						allDefendants={allDefendants}
						autoFocus={pendingFocusField === 'Defendants'}
					/>
				),
					'Jurisdiction': (
						<AppTextInput
							className="parties-panel__compact-input"
							variant="borderless"
							autoFocus={pendingFocusField === 'Jurisdiction'}
							value={localJurisdiction}
							onChange={(e) => setLocalJurisdiction(e.target.value)}
						/>
					),
					'CaseNumber': (
						<AppTextInput
							className="parties-panel__compact-input"
							variant="borderless"
							autoFocus={pendingFocusField === 'CaseNumber'}
							value={localCaseNumber}
							onChange={(e) => setLocalCaseNumber(e.target.value)}
						/>
					),
					'default': (
						<AppTextInput
							className="parties-panel__compact-input"
							variant="borderless"
							value={attribute.value || ''}
						/>
				)
			}[attribute.key] || <AppTextInput variant="borderless" value={attribute.value || ''} />
		}

		if (attribute.key === 'Attorneys') {
			return Array.isArray(attribute.value) && attribute.value.length > 0
				? attribute.value.map((attorney) => attorney.Name).join(', ')
				: <span className="parties-panel__field-text parties-panel__field-text--muted">Not Available</span>
		}

		if (attribute.key === 'Defendants') {
			return Array.isArray(attribute.value) && attribute.value.length > 0
				? attribute.value.map((defendant) => defendant.PartyName).join(', ')
				: <span className="parties-panel__field-text parties-panel__field-text--muted">Not Available</span>
		}

		return attribute.value === null || attribute.value === undefined
			? <span className="parties-panel__field-text parties-panel__field-text--muted">Not Available</span>
			: attribute.value.toString()
	}

	useEffect(() => {
		if (isEditing && pendingFocusField) {
			setPendingFocusField(null)
		}
	}, [isEditing, pendingFocusField])

	return (
		<div className="col-12 xl:col-6">
			<div className={`parties-panel__shell parties-panel${isEditing ? ' is-editing' : ''}`}>
				<div className="parties-panel__section-header">
					<h2 className="parties-panel__section-title">Case Information</h2>
					<div className="parties-panel__compact-actions">
						{isEditing ? (
							<>
								<AppButton className="parties-panel__button" icon="pi pi-times" label="Cancel" variant="secondary" onClick={handleCancel} />
								<AppButton className="parties-panel__button" icon="pi pi-check" label="Save" variant="success" onClick={handleSave} />
							</>
						) : null}
					</div>
				</div>
				<div className="parties-panel__content">
					<div className={`parties-panel__field-grid${isEditing ? '' : ' parties-panel__field-grid--interactive'}`}>
						{attributes.map((attribute) => (
							<div
								key={attribute.key}
								className={`parties-panel__field${attribute.key === 'Stage' && !attributes.some((item) => item.key === 'Substage') ? ' parties-panel__field--full' : ''}`}
								onClick={() => handleStartEditingField(attribute.key)}
							>
								<div className="parties-panel__field-label">
									{attribute.label}
								</div>
								<div className={isEditing ? 'parties-panel__field-control' : 'parties-panel__field-value'}>
									{renderAttributeValue(attribute)}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);

}


export default CaseInfo;




function AttorneysSelect({ selectedAttorneys, setSelectedAttorneys, allAttorneys, autoFocus = false }) {
	const [filteredAttorneys, setFilteredAttorneys] = useState([]);
	const [query, setQuery] = useState('');

	const search =

	(event) => {
		setQuery(event.query); // update the query here
		setTimeout(() => {
			let _filteredAttorneys;
			if (!event.query.trim().length) {
				_filteredAttorneys = [...allAttorneys];
			} else {
				_filteredAttorneys = allAttorneys.filter((attorney) => {
					return attorney.Name.toLowerCase().includes(event.query.toLowerCase());
				});
			}
			setFilteredAttorneys(_filteredAttorneys);
		}, 250);
	}

	const handleKeyPress =

	(e) => {
		if (e.key === 'Enter' && query.length > 0) {
			const newItem = { Name: query };
			setSelectedAttorneys([...selectedAttorneys, newItem], 'attorneys');
			setQuery('');  // Clear the query
		}
	}



	return (
		<AppAutoComplete
			className="w-full parties-panel__compact-autocomplete"
			variant="borderless"
			autoFocus={autoFocus}
			value={selectedAttorneys}
			suggestions={filteredAttorneys}
			completeMethod={search}
			onChange={(e) => setSelectedAttorneys(e.value)}
			onKeyPress={handleKeyPress} // Listen for the Enter key
			field="Name"
			multiple
		/>
	);
}



function DefendantsSelect({ selectedDefendants, setSelectedDefendants, allDefendants, autoFocus = false }) {
	const [filteredDefendants, setFilteredDefendants] = useState([]);
	const [query, setQuery] = useState('');
	const [autoCompleteKey, setAutoCompleteKey] = useState(0);

	const search =

	(event) => {
		setQuery(event.query); // update the query here
		setTimeout(() => {
			let _filteredDefendants;
			if (!event.query.trim().length) {
				_filteredDefendants = [...allDefendants];
			} else {
				_filteredDefendants = allDefendants.filter((defendant) => {
					return defendant.PartyName.toLowerCase().includes(event.query.toLowerCase());
				});
			}
			setFilteredDefendants(_filteredDefendants);
		}, 250);
	};

	const handleKeyPress =

	(e) => {
		if (e.key === 'Enter' && query.length > 0) {
			const newItem = { PartyName: query };
			setSelectedDefendants([...selectedDefendants, newItem]);
			setQuery('');  // Clear the query
			setAutoCompleteKey(autoCompleteKey + 1);
		}
	};

	return (
		<AppAutoComplete
			className="w-full parties-panel__compact-autocomplete"
			variant="borderless"
			autoFocus={autoFocus}
			value={selectedDefendants}
			suggestions={filteredDefendants}
			completeMethod={search}
			onChange={(e) => setSelectedDefendants(e.value)}
			onKeyPress={handleKeyPress} // Listen for the Enter key
			field="PartyName"
			multiple
			key={autoCompleteKey}
		/>
	);
}



function SettledSubstage({ localSubstage, handleSelectChange }) {

	// Ok so I need to make this show different inputs depending on the substage
	// For Settled In Principal should have a deadline to execute agreement and a Trello card created 10 days before the deadline
	// For the Payment Pending we should have the ability to enter one or more deadlines and Trello cards created 5 days before each deadline.

	const [deadlineFormVisible, setDeadlineFormVisible] = useState(false);

	return (
		<>
			<TrelloTaskForm substage={localSubstage} visible={deadlineFormVisible} setVisible={setDeadlineFormVisible} />

			<li
				key="Substage"
				className="flex align-items-center py-0 px-2 border-top-1 surface-border flex-wrap"
			>
				<div className="text-500 w-6 md:w-6 py-3 font-medium">
					Substage
				</div>
				<div className="text-900 w-full md:w-6 md:flex-order-0 flex-order-1">
					<AppDropdown
						variant="borderless"
						value={localSubstage}
						options={Object.entries(settledSubstages).map(([key, value]) => ({ label: value, value: value }))}
						onChange={(e) => handleSelectChange(e.label, e.value, 'Substage')}
					/>
				</div>
			</li>

			{/* {localSubstage === 'Settled in Principal' && (
				<li
					key="Deadline"
					className="flex align-items-center py-0 px-2 border-top-1 surface-border flex-wrap"
				>
					<div className="text-500 w-6 md:w-6 py-3 font-medium">
						Deadlines
					</div>
					<div className="text-900 w-full md:w-6 md:flex-order-0 flex-order-1">
						<AppButton label="View/Edit Deadlines" onClick={() => setDeadlineFormVisible(true)} />
					</div>
				</li>
			)}

			{localSubstage === 'Agreement Executed & Payment(s) Pending' && (
				<li
					key="Deadline"
					className="flex align-items-center py-0 px-2 border-top-1 surface-border flex-wrap"
				>
					<div className="text-500 w-6 md:w-6 py-3 font-medium">
						Deadline
					</div>
					<div className="text-900 w-full md:w-6 md:flex-order-0 flex-order-1">
						<DatePicker />
					</div>
				</li>
			)} */}
		</>
	)
}
